/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
export var EuiRangeTooltip = ({
  value,
  valueAppend,
  valuePrepend,
  max,
  min,
  name,
  showTicks,
  compressed
}) => {
  const classes = classNames('euiRangeTooltip', {
    'euiRangeTooltip--compressed': compressed
  }); // Calculate the left position based on value

  let val = 0;

  if (typeof value === 'number') {
    val = value;
  } else if (typeof value === 'string') {
    val = parseFloat(value);
  }

  const decimal = (val - min) / (max - min); // Must be between 0-100%

  let valuePosition = decimal <= 1 ? decimal : 1;
  valuePosition = valuePosition >= 0 ? valuePosition : 0;
  let valuePositionSide;
  let valuePositionStyle;

  if (valuePosition > 0.5) {
    valuePositionSide = 'left';
    valuePositionStyle = {
      right: `${(1 - valuePosition) * 100}%`
    };
  } else {
    valuePositionSide = 'right';
    valuePositionStyle = {
      left: `${valuePosition * 100}%`
    };
  } // Change left/right position based on value (half way point)


  const valueClasses = classNames('euiRangeTooltip__value', `euiRangeTooltip__value--${valuePositionSide}`, {
    'euiRangeTooltip__value--hasTicks': showTicks
  });
  return <div className={classes}>
      <output className={valueClasses} htmlFor={name} style={valuePositionStyle}>
        {valuePrepend}
        {value}
        {valueAppend}
      </output>
    </div>;
};
EuiRangeTooltip.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number.isRequired, PropTypes.string.isRequired]),
  valueAppend: PropTypes.node,
  valuePrepend: PropTypes.node,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  name: PropTypes.string,
  showTicks: PropTypes.bool,
  compressed: PropTypes.bool
};

try {
  EuiRangeTooltip.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/range/range_tooltip.tsx',
    description: '',
    displayName: 'EuiRangeTooltip',
    methods: [],
    props: {
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        }],
        required: false,
        type: {
          name: 'string | number'
        }
      },
      valueAppend: {
        defaultValue: null,
        description: '',
        name: 'valueAppend',
        parent: {
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      valuePrepend: {
        defaultValue: null,
        description: '',
        name: 'valuePrepend',
        parent: {
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      max: {
        defaultValue: null,
        description: '',
        name: 'max',
        parent: {
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      min: {
        defaultValue: null,
        description: '',
        name: 'min',
        parent: {
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: {
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      showTicks: {
        defaultValue: null,
        description: '',
        name: 'showTicks',
        parent: {
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: null,
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/range/range_tooltip.tsx',
          name: 'EuiRangeTooltipProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['EuiRangeTooltipProps']
  };
} catch (e) {}